import React, { useState, useEffect, useCallback } from 'react';
import queryString from 'query-string';
import axios from 'axios';

import BasePageUAM from '../BasePageUAM/BasePageUAM';
import { PrsButtonLg, PrsOutlineButtonLg, PrsButtonStackLg } from '../../styles-and-themes/styles/prsButtons';
import {
  UamHeading,
  UamBodyText,
  UamFlexColumn,
  UamFormSection,
  UamLoadingContainer,
  UamPageIcon,
} from '../../styles-and-themes/styles/uam-registration-styles';
import UamBanner from '../../components/PrsAlert/UamBanner';
import {
  PrsFormControl,
  PrsLabel,
  PrsLabelText,
} from '../../styles-and-themes/styles/prsInputs';
import MfaVerifyPanel from '../LoginUAMPage/Panels/MfaVerifyPanel/MfaVerifyPanel';
import SecurityQuestionPanel from '../../shared-panels/uam/SecurityQuestionPanel/SecurityQuestionPanel';
import ProseSpinner from '../../components/ProseSpinner/ProseSpinner';
import StepTransitionOverlay from '../../components/StepTransitionOverlay/StepTransitionOverlay';
import PrsResetGlobalStyle from '../../styles-and-themes/styles/prsReset';
import { validatePassword } from '../../../shared-with-server/validation-utils';
import { getFullNameFromShortName, getShortProductName } from '../../../shared-with-server/product-name-utils';
import { destinationInfo, frontEndUrl } from '../../destructured-front-end-config';
import { composeDestinationQuerystring } from '../../../shared-with-server/destination-utils';
import { isJwtValid, safeDecodeJwt } from '../../utils/jwt-utils';
import { EyeIcon, EyeOffIcon } from '../../components/icons/EyeIcons';
import { VisuallyHidden } from '../../styles-and-themes/styles/visually-hidden';
import {
  PasswordFieldWrap,
  PasswordInput,
  ToggleVisibility,
  RequirementsList,
  Requirement,
} from './ResetPasswordUAMPageStyles';

const STEP_LOADING = 'loading';
const STEP_MFA = 'mfa';
const STEP_SECURITY_QUESTION = 'securityQuestion';
const STEP_FORM = 'form';
const STEP_SUCCESS = 'success';
const STEP_EXPIRED = 'expired';

const FLOW_NEXT = '/api/v2/fi/flows/password-reset/next';

const GPP_MAYBE_ICON_URL = `${frontEndUrl}static/images/gpp_maybe.svg`;
const CHECK_CIRCLE_ICON_URL = `${frontEndUrl}static/images/check_circle.svg`;

const ResetPasswordUAMPage = () => {
  const [encodedKey] = useState(() => queryString.parse(location.search)?.key);
  const [keyIsValid] = useState(() => isJwtValid(encodedKey));

  // dest/path for login link — read from JWT client-side just for URL construction (safe, not auth)
  const [destPath] = useState(() => {
    const decoded = safeDecodeJwt(encodedKey) || {};
    return { dest: decoded.dest, path: decoded.path, retURL: decoded.retURL || '/' };
  });

  const getBrandInfo = () => {
    const shortProductName = getShortProductName(destinationInfo, location.host, destPath);
    return { short: shortProductName, full: getFullNameFromShortName(shortProductName) };
  };
  const [brand] = useState(getBrandInfo);

  const [step, setStep] = useState(() => (keyIsValid ? STEP_LOADING : STEP_EXPIRED));
  // const [mfaData, setMfaData] = useState(false); // TODO: used when MFA panel is wired up
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [securityQuestionText, setSecurityQuestionText] = useState(null);
  const [securityQuestionError, setSecurityQuestionError] = useState(null);
  const [securityQuestionSubmitting, setSecurityQuestionSubmitting] = useState(false);

  const loginUrl = `/uam/login?${composeDestinationQuerystring(destinationInfo, destPath)}`;
  const postResetLoginUrl = `/uam/login?postReset=1&${composeDestinationQuerystring(destinationInfo, destPath)}`;
  const validity = validatePassword(password);
  const allReqsMet = Object.values(validity).every(Boolean);
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;
  const isFormValid = allReqsMet && passwordsMatch;

  // On mount, resume from the link token to establish the flow session
  useEffect(() => {
    if (!keyIsValid) return undefined;

    const controller = new AbortController();

    axios.post(FLOW_NEXT, {
      action: 'RESUME_FROM_LINK',
      payload: { flowToken: encodedKey },
    }, { signal: controller.signal }).then((res) => {
      const { ok, state /* , data */ } = res.data; // data unused until MFA panel is wired up
      if (ok && state === 'MFA_REQUIRED') {
        // setMfaData(true); // TODO: pass maskedPhone data to MFA panel
        setStep(STEP_MFA);
      } else if (ok && state === 'SET_PASSWORD') {
        setStep(STEP_FORM);
      } else {
        setStep(STEP_EXPIRED);
      }
    }).catch((err) => {
      if (axios.isCancel(err)) return;
      const status = err?.response?.status;
      if (status === 401 || status === 400) {
        setStep(STEP_EXPIRED);
      } else {
        setErrorMessage('Something went wrong. Please try again.');
        setStep(STEP_EXPIRED);
      }
    });

    return () => controller.abort();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onMfaSuccess = async () => {
    // MFA verified via SMS endpoint — tell the flow to advance to SET_PASSWORD
    try {
      const res = await axios.post(FLOW_NEXT, { action: 'VERIFY_MFA_CODE' });
      if (res.data?.ok && res.data?.state === 'SET_PASSWORD') {
        setStep(STEP_FORM);
      } else {
        setStep(STEP_EXPIRED);
      }
    } catch (_err) {
      setStep(STEP_EXPIRED);
    }
  };

  // "Verify Another Way" — forward-only switch from OTP to security questions.
  // Backend clears mfaLoginToken when this fires; the user can't return to OTP
  // without restarting the flow.
  const onVerifyAnotherWay = async () => {
    try {
      const res = await axios.post(FLOW_NEXT, { action: 'VERIFY_ANOTHER_WAY' });
      if (res.data?.ok && res.data?.state === 'SECURITY_QUESTION') {
        setSecurityQuestionText(res.data.data?.questionText || null);
        setSecurityQuestionError(null);
        setStep(STEP_SECURITY_QUESTION);
      } else {
        setStep(STEP_EXPIRED);
      }
    } catch (_err) {
      setStep(STEP_EXPIRED);
    }
  };

  const onSecurityAnswerSubmit = async (answerText) => {
    if (!answerText?.length || securityQuestionSubmitting) return;
    setSecurityQuestionSubmitting(true);
    setSecurityQuestionError(null);
    try {
      const res = await axios.post(FLOW_NEXT, {
        action: 'SUBMIT_ANSWER',
        payload: { answerText },
      });
      const {
        ok, state, data, errors,
      } = res.data || {};
      if (ok && state === 'SET_PASSWORD') {
        setStep(STEP_FORM);
        return;
      }
      if (!ok && state === 'SECURITY_QUESTION') {
        setSecurityQuestionText(data?.questionText || securityQuestionText);
        setSecurityQuestionError(errors?.[0] || 'Your answer was not correct.');
        return;
      }
      // Rate-limited / session lost / other — drop to expired so the user starts over.
      setStep(STEP_EXPIRED);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        // checkSecurityAnswer ran out of questions — terminal for this session.
        setStep(STEP_EXPIRED);
      } else {
        setSecurityQuestionError('Something went wrong. Please try again.');
      }
    } finally {
      setSecurityQuestionSubmitting(false);
    }
  };

  const onSubmit = async (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    if (!isFormValid || isSubmitting) return;

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const res = await axios.post(FLOW_NEXT, {
        action: 'SET_NEW_PASSWORD',
        payload: { password },
      });

      const { ok, state } = res.data;
      if (ok && state === 'SUCCESS') {
        setStep(STEP_SUCCESS);
        // Brief pause so the success screen is visible, then hand off to LoginUAMPage
        // which will auto-complete the login via the mfaLoginToken cookie.
        setTimeout(() => { window.location.href = postResetLoginUrl; }, 1500);
      } else {
        setErrorMessage(res.data?.errors?.[0] || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setErrorMessage(err?.response?.data?.errors?.[0] || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Move keyboard focus to each branch's heading on transition. The callback ref
  // fires once per mount, so swapping branches automatically lands focus on the
  // new heading without an extra useEffect tied to `step`.
  const focusHeadingOnMount = useCallback((node) => {
    if (!node) return;
    if (!node.hasAttribute('tabindex')) node.setAttribute('tabindex', '-1');
    node.focus({ preventScroll: false });
  }, []);

  let content;
  if (step === STEP_LOADING) {
    content = (
      <UamLoadingContainer>
        <ProseSpinner size="5rem" />
      </UamLoadingContainer>
    );
  } else if (step === STEP_SUCCESS) {
    content = (
      <UamFlexColumn gap="16px">
        <UamPageIcon src={CHECK_CIRCLE_ICON_URL} alt="" />
        <UamHeading ref={focusHeadingOnMount}>You&apos;re all set!</UamHeading>
        <UamBodyText>Your password has been updated. Signing you in now.</UamBodyText>
      </UamFlexColumn>
    );
  } else if (step === STEP_EXPIRED) {
    content = (
      <UamFlexColumn gap="16px">
        <UamPageIcon src={GPP_MAYBE_ICON_URL} alt="" />
        <UamHeading ref={focusHeadingOnMount}>Verification link no longer valid.</UamHeading>
        <UamBodyText>
          This verification link has expired or has already been used. Please return to sign in and request a new verification link.
        </UamBodyText>
        <PrsButtonLg
          type="button"
          style={{ width: '100%', marginTop: '8px' }}
          onClick={() => { window.location.href = loginUrl; }}
        >
          Back To Sign In
        </PrsButtonLg>
      </UamFlexColumn>
    );
  } else if (step === STEP_MFA) {
    content = (
      <MfaVerifyPanel
        onSuccess={onMfaSuccess}
        onBack={() => { window.location.href = loginUrl; }}
        onVerifyAnotherWay={onVerifyAnotherWay}
      />
    );
  } else if (step === STEP_SECURITY_QUESTION) {
    content = (
      <SecurityQuestionPanel
        questionText={securityQuestionText || ''}
        onSubmit={onSecurityAnswerSubmit}
        isSubmitting={securityQuestionSubmitting}
        errorMessage={securityQuestionError}
        onBack={() => { window.location.href = loginUrl; }}
      />
    );
  } else {
    content = (
      <StepTransitionOverlay loading={isSubmitting}>
        <UamFlexColumn gap="0">
          {errorMessage && (
            <UamBanner variant="danger">{errorMessage}</UamBanner>
          )}
          <UamFlexColumn gap="16px" padding={errorMessage ? '24px 0 0' : '0'}>
            <UamHeading ref={focusHeadingOnMount}>Reset Password</UamHeading>
            <form onSubmit={onSubmit}>
              <UamFormSection>
                <UamBodyText>Please enter a new password.</UamBodyText>
                <UamFlexColumn gap="16px">
                  <PrsFormControl $error={!!errorMessage}>
                    <PrsLabel>
                      <PrsLabelText>Create New Password</PrsLabelText>
                    </PrsLabel>
                    <PasswordFieldWrap>
                      <PasswordInput
                        id="new-password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        autoComplete="new-password"
                        onChange={(ev) => setPassword(ev.target.value)}
                        $error={!!errorMessage}
                        aria-describedby="reset-pw-requirements"
                      />
                      <ToggleVisibility
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        aria-pressed={showPassword}
                        aria-controls="new-password"
                      >
                        {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                      </ToggleVisibility>
                    </PasswordFieldWrap>
                    <RequirementsList
                      id="reset-pw-requirements"
                      aria-label="Password requirements"
                      aria-live="polite"
                      aria-atomic="false"
                    >
                      <Requirement $met={validity.eight} aria-label={`${validity.eight ? 'Met' : 'Not met'}: 8 to 72 characters`}>8-72 characters</Requirement>
                      <Requirement $met={validity.upper} aria-label={`${validity.upper ? 'Met' : 'Not met'}: uppercase letter`}>Uppercase letter</Requirement>
                      <Requirement $met={validity.special} aria-label={`${validity.special ? 'Met' : 'Not met'}: special character`}>Special character</Requirement>
                      <Requirement $met={validity.number} aria-label={`${validity.number ? 'Met' : 'Not met'}: number`}>Number</Requirement>
                      <Requirement $met={validity.lower} aria-label={`${validity.lower ? 'Met' : 'Not met'}: lowercase letter`}>Lowercase letter</Requirement>
                    </RequirementsList>
                  </PrsFormControl>
                  <PrsFormControl $error={confirmPassword.length > 0 && !passwordsMatch}>
                    <PrsLabel>
                      <PrsLabelText>Confirm Password</PrsLabelText>
                    </PrsLabel>
                    <PasswordFieldWrap>
                      <PasswordInput
                        id="confirm-password"
                        type={showConfirm ? 'text' : 'password'}
                        value={confirmPassword}
                        autoComplete="new-password"
                        onChange={(ev) => setConfirmPassword(ev.target.value)}
                        $error={confirmPassword.length > 0 && !passwordsMatch}
                        aria-invalid={confirmPassword.length > 0 && !passwordsMatch}
                        aria-describedby={confirmPassword.length > 0 && !passwordsMatch ? 'confirm-password-error' : undefined}
                      />
                      <ToggleVisibility
                        type="button"
                        onClick={() => setShowConfirm((v) => !v)}
                        aria-label={showConfirm ? 'Hide password' : 'Show password'}
                        aria-pressed={showConfirm}
                        aria-controls="confirm-password"
                      >
                        {showConfirm ? <EyeOffIcon /> : <EyeIcon />}
                      </ToggleVisibility>
                    </PasswordFieldWrap>
                    {confirmPassword.length > 0 && !passwordsMatch && (
                      <VisuallyHidden id="confirm-password-error" role="alert">
                        Passwords don&apos;t match.
                      </VisuallyHidden>
                    )}
                  </PrsFormControl>
                </UamFlexColumn>
              </UamFormSection>
              <PrsButtonStackLg>
                <PrsButtonLg
                  type="submit"
                  style={{ width: '100%' }}
                  disabled={!isFormValid || isSubmitting}
                >
                  Reset Password
                </PrsButtonLg>
                <PrsOutlineButtonLg
                  type="button"
                  style={{ width: '100%' }}
                  onClick={() => { window.location.href = loginUrl; }}
                >
                  Back to Sign In
                </PrsOutlineButtonLg>
              </PrsButtonStackLg>
            </form>
          </UamFlexColumn>
        </UamFlexColumn>
      </StepTransitionOverlay>
    );
  }

  return (
    <>
      {/* Page-scoped reset; lift to App.js once verified safe across all pages. */}
      <PrsResetGlobalStyle />
      <BasePageUAM
        brand={brand}
        destPath={destPath}
        showTabs={false}
      >
        {content}
      </BasePageUAM>
    </>
  );
};

export default ResetPasswordUAMPage;
